import type { QueryClient } from "@tanstack/react-query";

/**
 * Every query prefix that renders a route's status or progress OUTSIDE the
 * route's own detail page (BUG-738 / BUG-739).
 *
 * The global `staleTime` is 5 minutes (`src/lib/queryClient.ts`), so a list a
 * dispatcher left behind is served from the cache on return WITHOUT a refetch
 * unless something marked it stale. Each entry is a PREFIX: invalidating
 * `["routes"]` matches every filter/page variant of the table, the filter
 * modal's counts and the schedule calendar.
 *
 * - `["routes"]`              — routes table, filter-modal counts, schedule calendar.
 * - `["routes-map"]`          — routes map pins + KPI strip (separate key, BUG-297).
 * - `["route-stops-map"]`     — stop pins on the routes map (colour = stop status).
 * - `["routes-today"]`        — dashboard «today's routes» list.
 * - `["dashboard-stats"]`     — dashboard route aggregates.
 * - `["routes-combobox"]`     — route picker; filtered by status (planned/active).
 * - `["chat", "threads"]`     — chat thread rows carry the driver's route progress.
 * - `["chat", "thread-context"]` — chat side panel «Текущий рейс» (BUG-739).
 *
 * Invalidation only REFETCHES queries that are currently mounted; an unmounted
 * one is just marked stale and refetches when its view mounts again. That is
 * what makes calling this on every route write cheap.
 */
export const ROUTE_LIST_QUERY_PREFIXES: ReadonlyArray<readonly unknown[]> = [
  ["routes"],
  ["routes-map"],
  ["route-stops-map"],
  ["routes-today"],
  ["dashboard-stats"],
  ["routes-combobox"],
  ["chat", "threads"],
  ["chat", "thread-context"],
];

/**
 * Marks every route-list-shaped view stale after a route's status or stop
 * progress changed — and, when `routeId` is given, that route's detail queries
 * (`["route", id]`, `["route-stops", id]`) too.
 *
 * The ONE place to call after any route status write (start, complete, archive,
 * cancel/disband, release, hold, delete), after any stop-status write (the
 * backend may auto-complete the route in the same request), and on a live
 * `route_status_update` frame. Do NOT call it on GPS `location_update` frames:
 * a position ping changes no route status.
 *
 * Returns a promise that settles when the active refetches do, so a mutation's
 * `onSuccess` can await it.
 */
export function invalidateRouteLists(
  queryClient: QueryClient,
  routeId?: number,
): Promise<void> {
  const keys: Array<readonly unknown[]> = [...ROUTE_LIST_QUERY_PREFIXES];
  if (routeId !== undefined && !Number.isNaN(routeId)) {
    keys.push(["route", routeId], ["route-stops", routeId]);
  }
  return Promise.all(
    keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
  ).then(() => undefined);
}
